import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import DeploymentCoverage from "./DeploymentCoverage";
import {
  Loader2, Clock, Package as PackageIcon, ShieldAlert, TrendingUp,
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, History, GitBranch,
} from "lucide-react";

/**
 * Drill-down slide-out for a single deployment.
 *
 * Renders:
 *  - stage timeline (oldest -> newest)
 *  - classified failure breakdown (top bars)
 *  - full per-device install state table
 * Feed by GET /api/deployments/{package_id}/drill-down.
 */

const TYPE_ICON = {
  "created":         PackageIcon,
  "packaged":        PackageIcon,
  "deployed":        CheckCircle2,
  "auto-updated":    RefreshCw,
  "status-refresh":  RefreshCw,
  "failure-refresh": ShieldAlert,
};
const TYPE_COLOR = {
  "created":         "#94A3B8",
  "packaged":        "#00D4E4",
  "deployed":        "#10B981",
  "auto-updated":    "#F59E0B",
  "status-refresh":  "#94A3B8",
  "failure-refresh": "#F43F5E",
};
// Rollout stage events are "phase1".."phaseN": a deployment pipeline has
// 1-10 stages after UAT, so match the pattern rather than listing keys.
const isPhaseEvent = (type) => /^phase\d+$/.test(type || "");
const eventIcon = (type) => (isPhaseEvent(type) ? TrendingUp : TYPE_ICON[type] || Clock);
const eventColor = (type) => (isPhaseEvent(type) ? "#6376F0" : TYPE_COLOR[type] || "#94A3B8");

// A stage's name from the phased deployment's phases.phaseN ("Pilot",
// "Phase 2"); "Phase N" for rollouts started before deployment pipelines.
const stageNameFor = (phases, n) => {
  if (n == null) return null;
  const p = (phases || {})[`phase${n}`] || {};
  const named = p.predefined === true || p.stage_type === "group";
  return (named ? (p.name || p.group_name) : p.name) || `Phase ${n}`;
};

const formatWhen = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const DeviceStateBadge = ({ category, installState }) => {
  const map = {
    installed: { cls: "bg-emerald-500/20 text-emerald-300", label: "Installed" },
    pending:   { cls: "bg-amber-500/20 text-amber-300",     label: "Pending" },
    failed:    { cls: "bg-rose-500/20 text-rose-300",       label: "Failed" },
    // The user chose "Defer" (Win32 1602 / 0x80070642) — the install
    // hasn't failed, Intune retries on the next sync. Sky blue matches
    // the firmware Deployments tab.
    deferred:  { cls: "bg-sky-500/20 text-sky-300",         label: "Deferred" },
    unknown:   { cls: "bg-white/10 text-white/80",          label: installState || "Unknown" },
  };
  const m = map[category] || map.unknown;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${m.cls}`}>
      {m.label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Auto-progression audit trail
//
// Surfaces every decision the phased-deployment scheduler made for this
// deployment: did it promote to the next phase, was it below threshold,
// or was it skipped because Intune's report failed / device_count was
// missing. Each entry shows the exact inputs that drove the decision,
// including the version filter that defends against the auto-update
// detection-rule contamination bug (Intune reporting prior-version
// installs as "installed" for the new app).
// ---------------------------------------------------------------------------
const DECISION_STYLE = {
  promoted: {
    badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    label: "Promoted",
    Icon: TrendingUp,
  },
  below_threshold: {
    badge: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    label: "Held — below threshold",
    Icon: Clock,
  },
  skipped_missing_device_count: {
    badge: "bg-white/10 text-white/70 border-white/15",
    label: "Skipped (missing device_count)",
    Icon: AlertTriangle,
  },
  skipped_install_summary_failed: {
    badge: "bg-rose-500/20 text-rose-300 border-rose-500/30",
    label: "Skipped (Intune report failed)",
    Icon: XCircle,
  },
  error_promotion_failed: {
    badge: "bg-rose-500/20 text-rose-300 border-rose-500/30",
    label: "Promotion failed",
    Icon: XCircle,
  },
};

// phaseN_active (N up to 10) cycles through these.
const ACTIVE_PHASE_CLASSES = [
  "bg-blue-500/20 text-blue-300 border-blue-500/30",
  "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  "bg-purple-500/20 text-purple-300 border-purple-500/30",
];

const StatusBadge = ({ status, phases }) => {
  if (!status) return null;
  const map = {
    completed:      "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    complete:       "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    failed:         "bg-rose-500/20 text-rose-300 border-rose-500/30",
    in_progress:    "bg-amber-500/20 text-amber-300 border-amber-500/30",
  };
  const active = /^phase(\d+)_active$/.exec(status);
  const cls = active
    ? ACTIVE_PHASE_CLASSES[(Number(active[1]) - 1) % ACTIVE_PHASE_CLASSES.length]
    : map[status] || "bg-white/10 text-white/80 border-white/15";
  // "Pilot active" rather than "phase1 active" when the stage has a name.
  const label = active ? `${stageNameFor(phases, Number(active[1]))} active` : status.replace(/_/g, " ");
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${cls}`}>
      {label}
    </span>
  );
};

const PhaseAutoProgressAudit = ({ phased, formatWhen }) => {
  const entries = Array.isArray(phased.auto_progress_audit_log)
    ? [...phased.auto_progress_audit_log].reverse() // newest first
    : [];

  return (
    <section data-testid="drill-down-auto-progress-audit">
      <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3">
        <GitBranch className="w-4 h-4" /> Auto-progression audit
        <StatusBadge status={phased.status} phases={phased.phases} />
        {phased.auto_deployed && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
            auto-update
          </span>
        )}
        {phased.current_phase != null && (
          <span className="text-xs text-white/60 font-normal">
            currently {stageNameFor(phased.phases, phased.current_phase)}
          </span>
        )}
      </h3>

      <div className="rounded-lg border border-white/10 bg-white/5 p-3 mb-3 text-xs space-y-1" data-testid="drill-down-auto-progress-summary">
        <div className="flex items-center gap-2 text-white/80">
          <code className="text-[10px] bg-white/5 px-1.5 py-0.5 rounded">
            {phased.deployment_id || "—"}
          </code>
          {phased.version && (
            <span className="text-white/70">target v{phased.version}</span>
          )}
        </div>
        {phased.last_auto_progress_check && (
          <div className="text-white/60">
            Last check: <span className="text-white/80">{formatWhen(phased.last_auto_progress_check)}</span>
            {phased.last_auto_progress_result && (
              <span className="ml-2 text-emerald-300">· {phased.last_auto_progress_result}</span>
            )}
            {phased.last_auto_progress_error && (
              <span className="ml-2 text-rose-300">· {phased.last_auto_progress_error}</span>
            )}
          </div>
        )}
      </div>

      {entries.length === 0 ? (
        <p className="text-white/50 text-xs">
          No auto-progression checks have run against this deployment yet. The
          scheduler appends one entry per evaluation tick once the first stage
          after UAT is active.
        </p>
      ) : (
        <ol className="space-y-2" data-testid="drill-down-auto-progress-entries">
          {entries.map((e, i) => {
            const style = DECISION_STYLE[e.decision] || {
              badge: "bg-white/10 text-white/70 border-white/15",
              label: e.decision || "—",
              Icon: AlertTriangle,
            };
            const Icon = style.Icon;
            const rateClass =
              e.success_rate >= (e.threshold ?? 70)
                ? "text-emerald-300"
                : e.success_rate >= ((e.threshold ?? 70) * 0.5)
                  ? "text-amber-300"
                  : "text-rose-300";
            const versionFilterOn = e.version_filter_applied === true;
            const rawVsFiltered =
              versionFilterOn &&
              typeof e.intune_installed_raw === "number" &&
              typeof e.intune_installed === "number" &&
              e.intune_installed_raw !== e.intune_installed;
            return (
              <li
                key={i}
                className="rounded-md border border-white/10 bg-white/5 p-2.5"
                data-testid={`auto-progress-entry-${i}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${style.badge}`}
                    >
                      {style.label}
                    </span>
                    {/* Prefer the names the audit entry recorded; older
                        entries carry numbers only. */}
                    {e.phase != null && e.next_phase != null && (
                      <span className="text-xs text-white/70">
                        {e.phase_name || stageNameFor(phased.phases, e.phase)}
                        {" → "}
                        {e.next_phase_name || stageNameFor(phased.phases, e.next_phase)}
                      </span>
                    )}
                    {e.phase == null && e.from_phase != null && e.to && (
                      <span className="text-xs text-white/70">
                        {e.phase_name || stageNameFor(phased.phases, e.from_phase)} → {e.to}
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-white/50 shrink-0">
                    {formatWhen(e.ts || e.timestamp)}
                  </span>
                </div>

                {typeof e.success_rate === "number" && (
                  <div className="mt-1.5 text-xs flex items-baseline gap-2">
                    <span className={`font-semibold ${rateClass}`}>
                      {e.success_rate}%
                    </span>
                    <span className="text-white/60">
                      ({e.effective_installed ?? "?"} / {e.phase_device_count ?? "?"} installed)
                    </span>
                    {typeof e.threshold === "number" && (
                      <span className="text-white/40">vs {e.threshold}% threshold</span>
                    )}
                  </div>
                )}

                {rawVsFiltered && (
                  <div
                    className="mt-1.5 text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1"
                    title="Intune reported devices as installed that were actually still on the previous version. The version filter excluded them."
                    data-testid={`auto-progress-version-filter-${i}`}
                  >
                    Version filter excluded {e.intune_installed_raw - e.intune_installed} prior-version "installed" device(s)
                    {e.deployment_version && (
                      <> (target v<code className="bg-black/30 px-1 rounded">{e.deployment_version}</code>)</>
                    )}
                  </div>
                )}

                {e.error && (
                  <div className="mt-1.5 text-[11px] text-rose-300 break-all">
                    {e.error}
                  </div>
                )}

                {e.intune_by_version && Object.keys(e.intune_by_version).length > 0 && (
                  <details className="mt-1.5 text-[11px] text-white/60">
                    <summary className="cursor-pointer hover:text-white/80">
                      Intune install state by version
                    </summary>
                    <ul className="mt-1 pl-3 space-y-0.5">
                      {Object.entries(e.intune_by_version).map(([ver, st]) => (
                        <li key={ver}>
                          <code className="bg-white/5 px-1 rounded">{ver}</code>
                          <span className="ml-2 text-emerald-300">{st.installed || 0} installed</span>
                          {st.pending > 0 && <span className="ml-2 text-amber-300">{st.pending} pending</span>}
                          {st.failed > 0 && <span className="ml-2 text-rose-300">{st.failed} failed</span>}
                          <span className="ml-2 text-white/40">· total {st.total}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                <details className="mt-1.5 text-[11px] text-white/40">
                  <summary className="cursor-pointer hover:text-white/60">Raw inputs</summary>
                  <pre className="mt-1 text-[10px] bg-black/30 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(e, null, 2)}
                  </pre>
                </details>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};



const DeploymentDrillDown = ({ open, onOpenChange, packageId }) => {
  const { getAccessToken, demoMode } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  // Bumped by Refresh so the coverage section reloads with the panel.
  const [coverageRun, setCoverageRun] = useState(0);

  // Pull the drill-down payload. `force=true` adds ?refresh=true so the
  // backend re-fetches from Graph even if its cache is populated.
  const getHeaders = async () => (demoMode ? {} : (await getAccessToken()
    .then((t) => (t ? { Authorization: `Bearer ${t}` } : {}))
    .catch(() => ({}))));

  const loadData = async ({ force = false, signal } = {}) => {
    if (force) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const res = await axios.get(
        `${API}/deployments/${packageId}/drill-down${force ? "?refresh=true" : ""}`,
        { headers, signal },
      );
      setData(res.data);
    } catch (e) {
      if (axios.isCancel?.(e) || e?.name === "CanceledError") return;
      console.error("drill-down load failed", e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        setError(detail || "This deployment could not be found in the current tenant.");
      } else if (detail) {
        setError(detail);
      } else if (e?.message) {
        setError(`Could not load drill-down: ${e.message}`);
      } else {
        setError("Could not load drill-down");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!open || !packageId) return;
    const controller = new AbortController();
    loadData({ signal: controller.signal });
    return () => controller.abort();
  }, [open, packageId, demoMode]); // loadData intentionally omitted (recreated each render)

  const pkg = data?.package || {};
  const failureMax = data?.failure_breakdown?.length
    ? Math.max(...data.failure_breakdown.map((f) => f.count || 0))
    : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto bg-[#0F172A] text-white border-l border-white/10"
        data-testid="deployment-drill-down"
      >
        <SheetHeader className="pb-3 border-b border-white/10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="text-white text-xl">
                {pkg.app_name || "Deployment detail"}
                {pkg.version && <span className="ml-2 text-white/60 text-sm">v{pkg.version}</span>}
              </SheetTitle>
              <SheetDescription className="text-white/70">
                {pkg.publisher || ""}{pkg.intune_app_id ? ` · ${pkg.intune_app_id}` : ""}
              </SheetDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { loadData({ force: true }); setCoverageRun((n) => n + 1); }}
              disabled={loading || refreshing || !packageId}
              data-testid="drill-down-refresh-btn"
              className="shrink-0 bg-white/5 border-white/15 text-white hover:bg-white/10"
              title="Force a fresh fetch from Microsoft Graph"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </SheetHeader>

        {loading && (
          <div className="py-16 flex items-center justify-center text-white/70" data-testid="drill-down-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        )}

        {error && !loading && (
          <div className="py-8 text-rose-400 text-sm" data-testid="drill-down-error">{error}</div>
        )}

        {data && !loading && (
          <div className="py-4 space-y-6">
            {/* Stats strip */}
            <div className="grid grid-cols-5 gap-2 text-center">
              {[
                { label: "Total", value: data.stats.total ?? 0, color: "#CBD5E1" },
                { label: "Installed", value: data.stats.installed ?? 0, color: "#10B981" },
                { label: "Pending", value: data.stats.pending ?? 0, color: "#F59E0B" },
                { label: "Deferred", value: data.stats.deferred ?? 0, color: "#38BDF8" },
                { label: "Failed", value: data.stats.failed ?? 0, color: "#F43F5E" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg bg-white/5 p-2" data-testid={`drill-down-stat-${s.label.toLowerCase()}`}>
                  <div className="text-[10px] uppercase tracking-wider text-white/60">{s.label}</div>
                  <div className="text-xl font-semibold" style={{ color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Timeline */}
            <section data-testid="drill-down-timeline">
              <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4" /> Timeline
              </h3>
              {data.timeline.length === 0 ? (
                <p className="text-white/50 text-xs">No events recorded yet.</p>
              ) : (
                <ol className="relative border-l border-white/10 ml-2 space-y-4">
                  {data.timeline.map((ev, i) => {
                    const Icon = eventIcon(ev.type);
                    const color = eventColor(ev.type);
                    return (
                      <li key={i} className="ml-4">
                        <span
                          className="absolute -left-[9px] mt-1 w-4 h-4 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${color}30`, border: `1px solid ${color}` }}
                        >
                          <Icon className="w-2.5 h-2.5" style={{ color }} />
                        </span>
                        <div className="text-sm text-white">{ev.label}</div>
                        <div className="text-xs text-white/50">{formatWhen(ev.at)}</div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>

            {/* Outcome by previous version */}
            {Array.isArray(data.previous_version_breakdown) && data.previous_version_breakdown.length > 0 && (
              <section data-testid="drill-down-prior-versions">
                <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3">
                  <History className="w-4 h-4" /> Outcome by previous version
                  <span className="text-xs text-white/60 font-normal">
                    (from {data.previous_version_meta?.source === "snapshot"
                      ? `estate snapshot ${data.previous_version_meta.snapshot_at ? "· " + formatWhen(data.previous_version_meta.snapshot_at) : ""}`
                      : data.previous_version_meta?.source === "current"
                        ? "current estate (no pre-deployment snapshot found)"
                        : "no data"})
                  </span>
                </h3>
                {(() => {
                  const rows = data.previous_version_breakdown;
                  const maxTotal = Math.max(...rows.map((r) => r.total || 0), 1);
                  return (
                    <div className="space-y-2">
                      {rows.map((r) => (
                        <div key={r.version} data-testid={`prior-version-${r.version}`}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-white/90 flex items-center gap-2">
                              <code className="text-[11px] bg-white/5 px-1.5 py-0.5 rounded">
                                {r.version}
                              </code>
                              <span className="text-white/60">
                                {r.total} device{r.total === 1 ? "" : "s"}
                              </span>
                            </span>
                            <span className="text-white/70 flex items-center gap-3">
                              {r.installed > 0 && (
                                <span className="text-emerald-300">{r.installed} installed</span>
                              )}
                              {r.pending > 0 && (
                                <span className="text-amber-300">{r.pending} pending</span>
                              )}
                              {r.deferred > 0 && (
                                <span className="text-sky-300">{r.deferred} deferred</span>
                              )}
                              {r.failed > 0 && (
                                <span className="text-rose-300">{r.failed} failed</span>
                              )}
                              {r.failure_rate_pct > 0 && (
                                <span className="text-white/50">· {r.failure_rate_pct}% fail rate</span>
                              )}
                            </span>
                          </div>
                          <div
                            className="h-2 bg-white/5 rounded-full overflow-hidden flex"
                            style={{ width: `${Math.max(8, Math.round((r.total / maxTotal) * 100))}%` }}
                          >
                            {r.installed > 0 && (
                              <div
                                className="h-full bg-emerald-500/80"
                                style={{ width: `${(r.installed / r.total) * 100}%` }}
                                title={`${r.installed} installed`}
                              />
                            )}
                            {r.pending > 0 && (
                              <div
                                className="h-full bg-amber-500/80"
                                style={{ width: `${(r.pending / r.total) * 100}%` }}
                                title={`${r.pending} pending`}
                              />
                            )}
                            {r.deferred > 0 && (
                              <div
                                className="h-full bg-sky-500/80"
                                style={{ width: `${(r.deferred / r.total) * 100}%` }}
                                title={`${r.deferred} deferred`}
                              />
                            )}
                            {r.failed > 0 && (
                              <div
                                className="h-full bg-rose-500/80"
                                style={{ width: `${(r.failed / r.total) * 100}%` }}
                                title={`${r.failed} failed`}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </section>
            )}

            {/* Failure breakdown */}
            <section data-testid="drill-down-failures">
              <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3">
                <ShieldAlert className="w-4 h-4" /> Failure reasons
                {data.total_failed > 0 && (
                  <span className="text-xs text-white/60 font-normal">({data.total_failed} failed)</span>
                )}
                {/* Deferrals are not failures, so they never appear in the
                    breakdown below — call the count out here so those
                    devices are still accounted for. */}
                {data.total_deferred > 0 && (
                  <span
                    className="text-xs text-sky-300 font-normal"
                    data-testid="drill-down-deferred-count"
                    title="Devices where the user chose Defer. Intune retries on the next sync — these are not failures."
                  >
                    · {data.total_deferred} deferred
                  </span>
                )}
              </h3>
              {data.failure_breakdown.length === 0 ? (
                <p className="text-white/50 text-xs">
                  No classified failures yet. The hourly scheduler will populate this
                  after Intune reports errors for this deployment.
                </p>
              ) : (
                <div className="space-y-2">
                  {data.failure_breakdown.map((f) => (
                    <div key={f.category} data-testid={`drill-down-failure-${f.category}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-white/90 flex items-center gap-2">
                          <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: f.color }}
                          />
                          {f.label}
                        </span>
                        <span className="text-white/70">{f.count} · {f.percent}%</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: failureMax
                              ? `${Math.round((f.count / failureMax) * 100)}%`
                              : "0%",
                            backgroundColor: f.color,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Auto-progression audit trail (phased deployments) */}
            {data.phased_deployment && (
              <PhaseAutoProgressAudit phased={data.phased_deployment} formatWhen={formatWhen} />
            )}

            {/* Coverage: targeted devices that never reported, with export.
                Keyed on the package (and Refresh) so it reloads fresh. */}
            {pkg.intune_app_id && (
              <DeploymentCoverage key={`${packageId}-${coverageRun}`} packageId={packageId} getHeaders={getHeaders} />
            )}

            {/* Devices */}
            <section data-testid="drill-down-devices">
              <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4" /> Devices
                <span className="text-xs text-white/60 font-normal">({data.devices.length})</span>
              </h3>
              {data.devices.length === 0 ? (
                <div className="text-xs space-y-2" data-testid="drill-down-devices-empty">
                  <p className="text-white/70">
                    {data.diagnostics?.reason || "No per-device data available."}
                  </p>
                  {data.diagnostics && (
                    <details className="text-white/50">
                      <summary className="cursor-pointer hover:text-white/70">
                        Diagnostic detail
                      </summary>
                      <ul className="mt-2 space-y-1 pl-4 list-disc">
                        <li>
                          Package linked to Intune:{" "}
                          <span className={data.diagnostics.has_intune_app_id ? "text-emerald-300" : "text-rose-300"}>
                            {data.diagnostics.has_intune_app_id ? "yes" : "no"}
                          </span>
                          {pkg.intune_app_id && (
                            <code className="ml-2 text-[10px] bg-white/5 px-1 py-0.5 rounded">
                              {pkg.intune_app_id}
                            </code>
                          )}
                        </li>
                        <li>
                          Tenant resolved:{" "}
                          <span className={data.diagnostics.has_tenant_id ? "text-emerald-300" : "text-rose-300"}>
                            {data.diagnostics.has_tenant_id ? "yes" : "no"}
                          </span>
                        </li>
                        <li>
                          App-level Graph token:{" "}
                          <span className={
                            data.diagnostics.token_status === "ok" ? "text-emerald-300"
                            : data.diagnostics.token_status === "unavailable" ? "text-rose-300"
                            : "text-white/50"
                          }>
                            {data.diagnostics.token_status || "—"}
                          </span>
                          {data.diagnostics.token_error && (
                            <div className="text-rose-300 text-[11px] mt-0.5 ml-4 break-all">
                              {data.diagnostics.token_error}
                            </div>
                          )}
                        </li>
                        <li>
                          Live Graph fetch:{" "}
                          <span className={
                            data.diagnostics.live_fetch_status === "ok" ? "text-emerald-300"
                            : data.diagnostics.live_fetch_status === "error" ? "text-rose-300"
                            : data.diagnostics.live_fetch_status === "no_devices" ? "text-amber-300"
                            : data.diagnostics.live_fetch_status === "summary_only" ? "text-amber-300"
                            : "text-white/50"
                          }>
                            {data.diagnostics.live_fetch_status || "—"}
                          </span>
                          {data.diagnostics.live_fetch_status_code != null && (
                            <span className="ml-2 text-[11px] text-white/70">
                              (HTTP {data.diagnostics.live_fetch_status_code}
                              {data.diagnostics.live_fetch_stage ? ` · ${data.diagnostics.live_fetch_stage}` : ""})
                            </span>
                          )}
                          {data.diagnostics.live_fetch_error && (
                            <div className="text-rose-300 text-[11px] mt-0.5 ml-4 break-all">
                              {data.diagnostics.live_fetch_error}
                            </div>
                          )}
                        </li>
                        {data.status_source && (
                          <li>Status source: <span className="text-white/70">{data.status_source}</span></li>
                        )}
                        {data.status_cached_at && (
                          <li>Cached at: <span className="text-white/70">{formatWhen(data.status_cached_at)}</span></li>
                        )}
                      </ul>
                    </details>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto border border-white/10 rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase text-white/60 bg-white/5">
                        <th className="px-3 py-2 font-medium">Device</th>
                        <th className="px-3 py-2 font-medium">User</th>
                        <th className="px-3 py-2 font-medium">State</th>
                        <th className="px-3 py-2 font-medium">Previous version</th>
                        <th className="px-3 py-2 font-medium">State Details</th>
                        {/* Intune's LastModifiedDateTime for the install state,
                            not the device's check-in (see Coverage for that). */}
                        <th className="px-3 py-2 font-medium">State changed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.devices.slice(0, 200).map((d, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="px-3 py-2 text-white/90">
                            {d.device_name || "—"}
                          </td>
                          <td className="px-3 py-2 text-white/70">{d.user || d.user_name || "—"}</td>
                          <td className="px-3 py-2">
                            <DeviceStateBadge category={d.category} installState={d.install_state} />
                          </td>
                          <td className="px-3 py-2 text-white/70">
                            {d.previous_version ? (
                              <code className="text-[11px] bg-white/5 px-1.5 py-0.5 rounded">
                                {d.previous_version}
                              </code>
                            ) : (
                              <span className="text-white/30">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-white/70">
                            {d.error_code ? (
                              <div className="flex flex-col gap-0.5">
                                <code className="text-[11px] bg-white/5 px-1.5 py-0.5 rounded self-start">
                                  {d.error_code}
                                </code>
                                {d.install_state_detail_meaning && (
                                  <span className={`text-[10px] ${
                                    d.install_state_detail_severity === "failed"
                                      ? "text-rose-300"
                                      : "text-white/50"
                                  }`}>
                                    {d.install_state_detail_meaning}
                                  </span>
                                )}
                              </div>
                            ) : d.install_state_detail ? (
                              <div className="flex flex-col gap-0.5">
                                <code className={`text-[11px] px-1.5 py-0.5 rounded self-start ${
                                  d.install_state_detail_severity === "failed"
                                    ? "bg-rose-500/10 text-rose-200"
                                    : "bg-white/5"
                                }`}>
                                  {d.install_state_detail}
                                </code>
                                {d.install_state_detail_meaning && (
                                  <span className={`text-[10px] ${
                                    d.install_state_detail_severity === "failed"
                                      ? "text-rose-300"
                                      : "text-white/50"
                                  }`}>
                                    {d.install_state_detail_meaning}
                                  </span>
                                )}
                              </div>
                            ) : ""}
                          </td>
                          <td className="px-3 py-2 text-white/60 text-xs">{formatWhen(d.last_sync)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {data.devices.length > 200 && (
                    <div className="px-3 py-2 text-xs text-white/50">
                      + {data.devices.length - 200} more devices (truncated)
                    </div>
                  )}
                </div>
              )}
            </section>

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default DeploymentDrillDown;
